import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/reservationCalendar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase/client";
import Header from "../Component/Header";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Link } from "react-router-dom";

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function ymd(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function pad(n) {
  return n < 10 ? `0${n}` : `${n}`;
}
function monthDayKey(year, monthIndex, day) {
  return `${year}-${pad(monthIndex + 1)}-${pad(day)}`;
}
function normalizeDateKey(value = "") {
  return String(value).slice(0, 10);
}

// 🔹 표시 유틸
const short4 = (s = "") => (s.length > 4 ? s.slice(0, 4) : s);
const hhmm = (t = "") => {
  // 허용: "HH:MM[:SS]" 또는 "HHMM"
  if (!t) return "";
  if (t.includes(":")) return t.split(":").slice(0, 2).join(":");
  if (t.length === 4) return `${t.slice(0, 2)}:${t.slice(2)}`;
  return t;
};

// 🔹 예약타입(한글) → 라우트 type 파라미터
const typeToParam = (label) => {
  switch (label) {
    case "통근": return "commute";
    case "투어": return "tour";
    case "학단": return "school";
    case "일반": return "general";
    default:     return "general";
  }
};

// 🔹 최종 링크 (App.jsx의 /dispatch/:type/:id 규격으로)
const editPath = (reservationType, id) => `/dispatch/${typeToParam(reservationType)}/${id}`;

export default function ReservationCalendar() {
  const [current, setCurrent] = useState(() => startOfMonth(new Date()));
  const [columnNum, setColumnNum] = useState(8); // 한 날짜 셀당 표시 최대 건수 (1~30 권장)
  const [items, setItems] = useState([]); // 이번 달 예약
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [category, setCategory] = useState("전체"); // ✅ 버튼 라벨
  const [, setType] = useState(""); // ✅ 내부 타입값
  const [reservationMode, setReservationMode] = useState("예약");
  const [selectedTentativeIds, setSelectedTentativeIds] = useState(new Set());

  const monthStart = useMemo(() => startOfMonth(current), [current]);
  const monthEnd = useMemo(() => endOfMonth(current), [current]);
  const monthStr = `${current.getFullYear()}-${pad(current.getMonth() + 1)}`;

  // 타입별 색상 클래스 (Tailwind)
  const TYPE_COLOR = {
    "일반": "text-slate-800",   // 기본 진회색
    "학단": "text-indigo-700",  // 남보라
    "통근": "text-emerald-700", // 초록
    "투어": "text-amber-700",   // 노랑/주황
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const company = localStorage.getItem("email"); // 회사 키(이메일)
      const pageSize = 1000;
      let from = 0;
      let allRows = [];
      const tableName = reservationMode === "가예약" ? "tentative_reservations" : "reservations";

      while (true) {
        let query = supabase
          .from(tableName)
          .select("id, date, user, time, arrival_time, seat, one_round, destination, reservation_type")
          .eq("company", company)
          .gte("date", ymd(monthStart))
          .lte("date", ymd(monthEnd))
          .order("date", { ascending: true })
          .order("time", { ascending: true })
          .range(from, from + pageSize - 1);

        // ✅ 카테고리 필터링
        if (category !== "전체") {
          query = query.eq("reservation_type", category);
        }

        const { data, error } = await query;
        if (error) throw error;

        const rows = data || [];
        allRows = allRows.concat(rows);
        if (rows.length < pageSize) break;
        from += pageSize;
      }

      setItems(allRows);
      setSelectedTentativeIds(new Set());
    } catch (e) {
      console.error("캘린더 조회 실패:", e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, category, reservationMode]);

  const toggleTentativeSelection = (id) => {
    setSelectedTentativeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmTentativeReservations = async () => {
    if (reservationMode !== "가예약" || selectedTentativeIds.size === 0 || confirming) return;

    const ok = window.confirm(`${selectedTentativeIds.size}건의 가예약을 예약으로 확정하시겠습니까?`);
    if (!ok) return;

    setConfirming(true);
    try {
      const selectedIds = Array.from(selectedTentativeIds);
      const company = localStorage.getItem("email") || "";
      const { data, error } = await supabase
        .from("tentative_reservations")
        .select("*")
        .eq("company", company)
        .in("id", selectedIds);

      if (error) throw error;
      if (!data || data.length === 0) {
        showUserAlert("확정할 가예약을 찾지 못했습니다.");
        return;
      }

      const rowsToInsert = data.map((row) => {
        const {
          id,
          created_at,
          updated_at,
          inserted_at,
          modified_at,
          ...reservationRow
        } = row;
        return reservationRow;
      });

      const { error: confirmError } = await supabase.rpc("confirm_tentative_reservations", {
        p_ids: selectedIds.map(String),
      });
      if (confirmError) throw confirmError;

      showUserAlert(`${rowsToInsert.length}건의 가예약을 예약으로 확정했습니다.`);
      setSelectedTentativeIds(new Set());
      await fetchReservations();
    } catch (e) {
      console.error("가예약 확정 실패:", e);
      showUserAlert("예약 확정 실패: " + (e.message || String(e)));
    } finally {
      setConfirming(false);
    }
  };

  // 해당 달의 날짜 배열(일~토 시작 6행 구성)
  const calendarCells = useMemo(() => {
    const days = [];
    const firstDow = monthStart.getDay(); // 0(일)~6(토)
    const prevPad = firstDow; // 앞 빈칸 개수
    const totalDays = monthEnd.getDate();
    const totalCells = Math.ceil((prevPad + totalDays) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - prevPad + 1;
      if (dayNum < 1 || dayNum > totalDays) {
        days.push(null);
      } else {
        const dateKey = monthDayKey(current.getFullYear(), current.getMonth(), dayNum);
        // 이 날짜의 예약
        const list = items.filter((r) => normalizeDateKey(r.date) === dateKey);
        days.push({ dayNum, dateKey, list });
      }
    }
    return days;
  }, [items, current, monthStart, monthEnd]);

  const onPrevMonth = () => {
    setCurrent((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  };
  const onNextMonth = () => {
    setCurrent((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  };

  return (
    <div className="space-y-1">
      <Header />

      {/* ✅ 예약 카테고리 버튼 */}
      <div className="flex px-4 py-1 items-center gap-4">
        <label className="font-semibold">구분</label>
        <div className="flex gap-2">
          {["전체", "일반", "학단", "통근", "투어"].map((label) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                setCategory(label);
                setType(
                  label === "통근"
                    ? "commute"
                    : label === "투어"
                    ? "tour"
                    : label === "학단"
                    ? "school"
                    : label === "일반"
                    ? "general"
                    : ""
                );
              }}
              className={
                category === label
                  ? "px-4 py-1 rounded-full border transition bg-blue-600 text-white"
                  : "px-4 py-1 rounded-full border transition bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ml-4 flex gap-2">
          {["예약", "가예약"].map((label) => (
            <button
              key={label}
              type="button"
              onClick={() => setReservationMode(label)}
              className={
                reservationMode === label
                  ? "px-4 py-1 rounded-full border transition bg-emerald-600 text-white"
                  : "px-4 py-1 rounded-full border transition bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-1 pb-4 space-y-2">
      <div className="ml-auto flex items-center gap-3 text-xs text-gray-600">
        <span><span className="inline-block w-3 h-3 rounded-full bg-slate-800 mr-1 align-middle" />일반</span>
        <span><span className="inline-block w-3 h-3 rounded-full bg-indigo-700 mr-1 align-middle" />학단</span>
        <span><span className="inline-block w-3 h-3 rounded-full bg-emerald-700 mr-1 align-middle" />통근</span>
        <span><span className="inline-block w-3 h-3 rounded-full bg-amber-700 mr-1 align-middle" />투어</span>
      </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onPrevMonth}>이전달</Button>
          <div className="font-semibold">{monthStr}</div>
          <Button variant="outline" onClick={onNextMonth}>다음달</Button>

          <div className="ml-6 flex items-center gap-2">
            <span className="text-sm">한 칸 최대 표시</span>
            <Input
              type="number"
              min={1}
              max={30}
              className="w-20"
              value={columnNum}
              onChange={(e) => {
                const v = Math.max(1, Math.min(30, Number(e.target.value || 0)));
                setColumnNum(v);
              }}
            />
          </div>
        </div>

        <Card>
          <CardContent className="bg-white p-2">
            {loading ? (
              <div className="p-4">불러오는 중...</div>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {/* 요일 헤더 */}
                {["일", "월", "화", "수", "목", "금", "토"].map((d) => (
                  <div key={d} className="text-center font-semibold py-2">{d}</div>
                ))}

                {/* 날짜 셀 */}
                {calendarCells.map((cell, idx) => {
                  if (!cell) {
                    return <div key={idx} className="min-h-28 border rounded p-2 bg-gray-50" />;
                  }
                  const { dayNum, dateKey, list } = cell;
                  const displayList = list.slice(0, columnNum);

                  return (
                    <div key={dateKey} className="min-h-28 border rounded p-2 flex flex-col">
                      <div className="text-sm font-semibold mb-1">
                        {dayNum}
                      </div>

                      <div className="flex-1 space-y-1">
                        {displayList.map((r) => {
                          const u = short4(r.user || "");
                          const t = hhmm(r.time || "");
                          const arrival = hhmm(r.arrival_time || "");
                          const dest = short4(r.destination || "");
                          const timeLabel = arrival ? `${t}~${arrival}` : t;
                          const line = `${u} - ${timeLabel} - ${r.seat || ""} - ${r.one_round || ""} - ${dest}`;

                          return reservationMode === "가예약" ? (
                            <label
                              key={r.id}
                              className={`flex items-center gap-1 text-xs truncate ${TYPE_COLOR[r.reservation_type] || "text-slate-800"}`}
                              title={line}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTentativeIds.has(r.id)}
                                onChange={() => toggleTentativeSelection(r.id)}
                                className="h-3 w-3 shrink-0"
                              />
                              <span className="truncate">{line}</span>
                            </label>
                          ) : (
                            <Link
                              key={r.id}
                              to={editPath(r.reservation_type, r.id)}
                              className={`block text-xs truncate hover:underline ${TYPE_COLOR[r.reservation_type] || "text-slate-800"}`}
                              title={line}
                            >
                              {line}
                            </Link>
                          );
                        })}
                        {list.length > columnNum && (
                          <div className="text-[11px] text-gray-500">… 외 {list.length - columnNum}건</div>
                        )}
                      </div>

                      <div className="mt-2 text-right text-xs text-gray-600">
                        총 {reservationMode} {list.length}대
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        {reservationMode === "가예약" && (
          <div className="flex justify-end">
            <Button
              onClick={handleConfirmTentativeReservations}
              disabled={confirming || selectedTentativeIds.size === 0}
              className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-300"
            >
              {confirming ? "확정 중..." : `예약 확정${selectedTentativeIds.size ? ` (${selectedTentativeIds.size})` : ""}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
